import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    title: "Para Aliados | Puerto Rico Esports League",
    description: "Explora oportunidades de colaboración con Puerto Rico Esports League en medios, eventos en vivo, contenido de marca y alianzas estratégicas.",
    ogDescription: "Resumen orientado a marcas, instituciones y colaboradores que quieran trabajar con PREL.",
    schemaName: "Para Aliados | Puerto Rico Esports League",
    schemaDescription: "Oportunidades de colaboración con Puerto Rico Esports League en medios, eventos en vivo y alianzas estratégicas.",
    eyebrow: "Para Aliados",
    heroTitle: "Una plataforma con la que las marcas pueden construir.",
    heroBody:
      "Puerto Rico Esports League ofrece mucho más que colocación de logo o auspicio de evento. PREL es una plataforma donde convergen gaming, cultura, experiencias en vivo, televisión y narrativa digital, creando espacio para alianzas visibles, relevantes y culturalmente alineadas.",
    ctaStart: "Iniciar conversación",
    ctaCases: "Ver casos seleccionados",
    whyEyebrow: "Por qué PREL",
    whyTitle: "Hecho en Puerto Rico. Diseñado para crecer.",
    whyBody1:
      "PREL fue construido en Puerto Rico, pero su formato está diseñado para ir más allá de una huella local. A través de una mezcla de televisión, narrativa digital y activaciones en vivo, PREL ofrece a sus aliados acceso a una plataforma centrada en gaming con valor cruzado en entretenimiento, cultura joven y posicionamiento de marca.",
    whyBody2:
      "Para los aliados correctos, PREL puede funcionar como plataforma de visibilidad, entorno para contenido de marca, motor de eventos en vivo y conversación estratégica sobre lo que los medios gaming pueden llegar a ser desde Puerto Rico.",
    connectEyebrow: "Por qué conecta",
    reasons: [
      "Plataforma nacida en Puerto Rico con relevancia para el mercado hispano en Estados Unidos y Latinoamérica",
      "Presencia multiformato en televisión, contenido digital y eventos en vivo",
      "Identidad reconocible en la intersección de gaming, cultura y entretenimiento",
      "Entorno de colaboración que combina visibilidad con valor narrativo y estratégico",
    ],
    pathsEyebrow: "Formas de trabajar con PREL",
    pathsTitle: "Caminos de colaboración",
    paths: [
      {
        title: "Auspicios de marca",
        body: "Integra tu marca con PREL a través de contenido, presencia en eventos, paquetes de visibilidad y oportunidades de narrativa de marca conectadas con audiencias afines al gaming.",
      },
      {
        title: "Activaciones en eventos en vivo",
        body: "Lleva tu marca a espacios presenciales de alta energía mediante activaciones en venue, momentos de interacción con audiencia y experiencias orientadas a comunidad.",
      },
      {
        title: "Colaboraciones de medios y contenido",
        body: "Explora segmentos originales, contenido auspiciado, cruces estratégicos y formatos narrativos construidos alrededor del programa y ecosistema digital de PREL.",
      },
      {
        title: "Conversaciones de crecimiento estratégico",
        body: "Para instituciones, grupos de medios y aliados de mayor escala interesados en visibilidad, construcción de ecosistema o expansión conectada a la plataforma PREL.",
      },
    ],
  },
  en: {
    title: "For Partners | Puerto Rico Esports League",
    description: "Explore collaboration opportunities with Puerto Rico Esports League across media, live events, branded content, and strategic partnerships.",
    ogDescription: "An overview for brands, institutions, and collaborators interested in working with PREL.",
    schemaName: "For Partners | Puerto Rico Esports League",
    schemaDescription: "Collaboration opportunities with Puerto Rico Esports League across media, live events, and strategic partnerships.",
    eyebrow: "For Partners",
    heroTitle: "A platform brands can build with.",
    heroBody:
      "Puerto Rico Esports League offers far more than logo placement or event sponsorship. PREL is a platform where gaming, culture, live experiences, television, and digital storytelling converge, creating room for visible, relevant, and culturally aligned partnerships.",
    ctaStart: "Start the conversation",
    ctaCases: "View selected work",
    whyEyebrow: "Why PREL",
    whyTitle: "Built in Puerto Rico. Designed to grow.",
    whyBody1:
      "PREL was built in Puerto Rico, but its format is designed to go beyond a local footprint. Through a mix of television, digital storytelling, and live activations, PREL gives partners access to a gaming-centered platform with crossover value in entertainment, youth culture, and brand positioning.",
    whyBody2:
      "For the right partners, PREL can function as a visibility platform, a branded content environment, a live-event engine, and a strategic conversation about what gaming media can become from Puerto Rico.",
    connectEyebrow: "Why it connects",
    reasons: [
      "A platform born in Puerto Rico with relevance for the U.S. Hispanic market and Latin America",
      "A multi-format presence across television, digital content, and live events",
      "A recognizable identity at the intersection of gaming, culture, and entertainment",
      "A collaboration environment that combines visibility with narrative and strategic value",
    ],
    pathsEyebrow: "Ways to work with PREL",
    pathsTitle: "Collaboration paths",
    paths: [
      {
        title: "Brand sponsorships",
        body: "Integrate your brand with PREL through content, event presence, visibility packages, and brand storytelling opportunities that connect with gaming-adjacent audiences.",
      },
      {
        title: "Live-event activations",
        body: "Bring your brand into high-energy in-person spaces through venue activations, audience interaction moments, and community-oriented experiences.",
      },
      {
        title: "Media and content collaborations",
        body: "Explore original segments, sponsored content, strategic crossovers, and storytelling formats built around PREL's show and digital ecosystem.",
      },
      {
        title: "Strategic growth conversations",
        body: "For institutions, media groups, and larger-scale partners interested in visibility, ecosystem building, or expansion connected to the PREL platform.",
      },
    ],
  },
};

export default function PartnerInquiryPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/for-partners", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/for-partners");

    const schemaId = "prel-for-partners-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: copy.schemaName,
      url: "https://www.puertoricoesportleague.com/for-partners",
      description: copy.schemaDescription,
      about: {
        "@type": "Organization",
        name: "Puerto Rico Esports League",
        url: "https://www.puertoricoesportleague.com",
      },
    });
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/for-partners" />

      <main>
        <section className="max-w-[1400px] mx-auto px-6 xl:px-8 pt-12 pb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
          <h1 className="mt-4 text-[clamp(2.6rem,8vw,6rem)] font-black leading-[0.9] uppercase">
            {copy.heroTitle}
          </h1>
          <p className="mt-6 max-w-4xl text-slate-300 text-lg leading-relaxed">
            {copy.heroBody}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/#contacto" className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-semibold hover:brightness-110 transition">
              {copy.ctaStart}
            </a>
            <a href="/selected-work" className="px-5 py-3 rounded-xl border border-white/15 text-white hover:bg-white/5 transition">
              {copy.ctaCases}
            </a>
          </div>
        </section>

        <section className="max-w-[1400px] mx-auto px-6 xl:px-8 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-6 items-start">
          <article className="border border-white/10 bg-[#111722] p-7 md:p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">{copy.whyEyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-black uppercase">{copy.whyTitle}</h2>
            <p className="mt-4 text-slate-300 leading-relaxed text-lg">{copy.whyBody1}</p>
            <p className="mt-4 text-slate-300 leading-relaxed">{copy.whyBody2}</p>
          </article>

          <article className="border border-white/10 bg-[#111722] p-7 md:p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.connectEyebrow}</p>
            <ul className="mt-4 space-y-3 text-slate-300 leading-relaxed">
              {copy.reasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          </article>
        </section>

        <section className="max-w-[1400px] mx-auto px-6 xl:px-8 pb-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.pathsEyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-black uppercase">{copy.pathsTitle}</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {copy.paths.map((item) => (
              <article key={item.title} className="border border-white/10 bg-[#111722] p-6 md:p-7">
                <h3 className="text-xl md:text-2xl font-bold">{item.title}</h3>
                <p className="mt-3 text-slate-300 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
